// FURNITURE-PLACEMENT toolbar — a small fixed bar shown while a piece is being placed/moved.
// MOVE and ROTATE switch what the pointer drag does (drag the piece around the room vs. spin it);
// PLACE confirms, CANCEL abandons. The world controller owns the actual drag logic.

export type PlacementMode = "move" | "rotate";

export interface PlacementBarActions {
  setMode: (mode: PlacementMode) => void;
  confirm: () => void;
  cancel: () => void;
}

const STYLE_ID = "ghostland-placebar-style";
const CSS = `
#gl-placebar {
  position: fixed; left: 50%; bottom: 84px; transform: translateX(-50%);
  z-index: 26; display: none; gap: 6px; padding: 8px;
  background: rgba(0,0,0,.85); border: 2px solid #f4f4f4; image-rendering: pixelated;
  font-family: 'Silkscreen', ui-monospace, monospace;
}
#gl-placebar.open { display: flex; }
#gl-placebar button {
  background: #000; border: 2px solid #6a6a6a; color: #b0b0b0;
  padding: 7px 12px; cursor: pointer; font-size: 9px; letter-spacing: 1px;
  font-family: inherit;
}
#gl-placebar button:hover { border-color: #f4f4f4; color: #f4f4f4; }
#gl-placebar button.active { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
#gl-placebar button.gl-pb-ok { border-color: #57d98a; color: #57d98a; }
#gl-placebar button.gl-pb-ok:hover { background: #57d98a; color: #000; }
#gl-placebar .gl-pb-hint {
  align-self: center; font-size: 8px; color: #7a7a7a; letter-spacing: 1px; padding: 0 6px;
}
`;

let bar: HTMLDivElement | null = null;
let moveBtn: HTMLButtonElement | null = null;
let rotBtn: HTMLButtonElement | null = null;
let hintEl: HTMLSpanElement | null = null;
let actions: PlacementBarActions | null = null;

const HINTS: Record<PlacementMode, string> = {
  move: "drag to move · scroll to raise/lower",
  rotate: "drag to spin",
};

function ensure(): void {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (bar) return;
  bar = document.createElement("div");
  bar.id = "gl-placebar";
  bar.innerHTML = `
    <button id="gl-pb-move">MOVE</button>
    <button id="gl-pb-rotate">ROTATE</button>
    <span class="gl-pb-hint" id="gl-pb-hint"></span>
    <button class="gl-pb-ok" id="gl-pb-place">PLACE</button>
    <button id="gl-pb-cancel">CANCEL</button>`;
  document.body.appendChild(bar);
  moveBtn = bar.querySelector("#gl-pb-move");
  rotBtn = bar.querySelector("#gl-pb-rotate");
  hintEl = bar.querySelector("#gl-pb-hint");
  moveBtn!.addEventListener("click", () => actions?.setMode("move"));
  rotBtn!.addEventListener("click", () => actions?.setMode("rotate"));
  bar.querySelector("#gl-pb-place")!.addEventListener("click", () => actions?.confirm());
  bar.querySelector("#gl-pb-cancel")!.addEventListener("click", () => actions?.cancel());
}

export function showPlacementBar(a: PlacementBarActions, mode: PlacementMode = "move"): void {
  ensure();
  actions = a;
  setPlacementBarMode(mode);
  bar!.classList.add("open");
}

/** Highlight the active mode button + swap the hint line. */
export function setPlacementBarMode(mode: PlacementMode): void {
  moveBtn?.classList.toggle("active", mode === "move");
  rotBtn?.classList.toggle("active", mode === "rotate");
  if (hintEl) hintEl.textContent = HINTS[mode];
}

export function hidePlacementBar(): void {
  bar?.classList.remove("open");
  actions = null;
}
